import { useEffect, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle2, AlertTriangle } from 'lucide-react'
import { SmbSeatsStep } from '../components/SmbSeatsStep'
import { API_BASE_URL } from '../lib/api'

// The owner's set-password email is said only when the status route says it
// left (backend smb_onboarding.public_status owner_invitation_sent).
export const OWNER_INVITED_LINE =
  'We have emailed you a link to set your DigitalWatch password.'

// DW-FE-M365-CALLBACK (docs/SECOND_TENANT_REHEARSAL.md BLIND-1).
//
// The calm landing the backend 302s to after it has processed the admin
// consent (see backend app/routers/onboarding.py::microsoft_consent_callback).
// Four backend redirect targets share this one page via two variants:
//   variant="success"  ← /onboarding/success?platform=m365
//                         /smb/consent-complete?session=…
//   variant="error"    ← /onboarding/error?platform=m365&error=consent_denied
//                         /smb/consent-failed?session=…&outcome=…
//
// The account is already updated server-side by the time we render; this page
// only tells the customer, honestly, what happened. It reads only the safe,
// backend-provided reason words — never tenant details.
//
// An Allow is a consent grant to the cloud's API — not a started interview and
// not a first meeting. The success copy must send the customer toward the
// first meeting (the interview), never tell them to close the window
// (DW-ALLOW-IS-NOT-THE-MEETING).
//
// DW-CONSENT-ALLOW-LANDS-ON-HOME-WITH-WORD-DECOY. The success variant used to
// say “Sign in below to begin” behind a single Link to “/”. That was a second
// door: a household that had just granted 365 was handed back to the sign-in
// paint and never reached /client home, so it never sat the incomplete first
// meeting in place and never saw “Download Decoy (.docx)” in that home
// section. DigitalWatch.md: know the person, and canaries are in the same
// picture — a tripwire the analyst sees with the logs, not a /client gimmick
// the model never reads. So the success primary control is now /client home
// (never “/”, and never the deep link /client/interview — ProtectedRoute and
// ClientInterviewGate still own who may sit the meeting). The page still does
// not start a meeting: home is where the meeting already sits, in one holding
// picture with the Word decoy download.
//
// DW-CLIENT-HOME-GWS-ALLOW-LANDS-ON-HOME-WITH-WORD-DECOY. The success copy
// used to be hard-wired to “Microsoft granted access” for every cloud. The
// firehose DigitalWatch.md holds is “3–4 weeks of everything we already
// ingest: Microsoft 365 and Google Workspace” — not 365 alone. A household
// that had just granted Google Workspace was told a Microsoft story and then
// sent home as if 365 were the whole product. So the success heading/body now
// name the cloud the `platform` query actually carries: Microsoft for the
// Microsoft platforms, Google Workspace for the Workspace ones. Both clouds
// keep the same single door to /client home, and both keep the honesty pin —
// naming the cloud is not claiming the loop is live on a real mailbox.
//
// DW-CLIENT-HOME-LITE-MEMBER-GWS-OAUTH-NAMES-WORKSPACE. Naming the cloud only
// works if the platform word the backend sends is one we read. The lite-member
// Google OAuth return sends the account's own tenant_type, 'personal_gmail',
// which was not in the Workspace set — so a household member who connected
// their Google account landed on the unnamed copy while the 'personal_microsoft'
// member beside them was named Microsoft. That is the firehose shrunk to 365 on
// the one screen that says which cloud we hold, so 'personal_gmail' is read as
// Google Workspace here. The backend now also sends the platform word on the
// lite-member error landings, which this page already names per cloud. Reading
// one more spelling changes no copy, no door and no honesty pin: both clouds
// still land on /client home with the first meeting and the Word decoy, an
// error still starts no meeting and never deep-links /client/interview, and an
// unrecognised platform is still left unnamed.

// The clouds the backend's `platform` query is allowed to name. The onboarding
// wizard uses the short 'm365' / 'gws' words; other callers use the long
// tenant_type spellings (microsoft365 / google_workspace); the lite-member
// OAuth returns use the personal tenant_types ('personal_microsoft',
// 'personal_gmail') on both the success and the error landing. Anything we do
// not recognise stays unnamed rather than being dressed up as one cloud or the
// other — an unknown platform must never pretend it was Microsoft.
const MICROSOFT_PLATFORMS = new Set([
  'm365',
  'ms365',
  'microsoft',
  'microsoft365',
  'personal_microsoft',
])
const GWS_PLATFORMS = new Set([
  'gws',
  'google',
  'workspace',
  'google_workspace',
  'googleworkspace',
  // The lite-member Google OAuth return (backend lite_member.py
  // ::google_oauth_callback) carries the account's own tenant_type, the mirror
  // of 'personal_microsoft' above. Without this read a household member who
  // had just connected their Google account landed unnamed — 'Access was
  // granted' — while the Microsoft one beside them was named, which is the
  // firehose shrunk to 365 on the one screen that says which cloud we hold.
  'personal_gmail',
])

type Cloud = 'microsoft' | 'gws' | 'unknown'

function cloudFor(platform: string | null, pathname: string): Cloud {
  const key = (platform ?? '').trim().toLowerCase()
  if (MICROSOFT_PLATFORMS.has(key)) return 'microsoft'
  if (GWS_PLATFORMS.has(key)) return 'gws'
  // The SMB self-serve return (/smb/consent-complete) carries only a session
  // token. It is emitted by exactly one place — the Microsoft admin-consent
  // callback — so the route itself, not a guess, is what names Microsoft here.
  if (!key && pathname.startsWith('/smb/')) return 'microsoft'
  return 'unknown'
}

// Success heading/body per cloud. The shape is identical for every cloud —
// naming Google Workspace must not buy the Workspace household a shorter or
// thinner page than the 365 household gets.
const GRANTED: Record<Cloud, { heading: string; granted: string }> = {
  microsoft: {
    heading: 'Microsoft granted access',
    granted: 'Microsoft has granted access',
  },
  gws: {
    heading: 'Google Workspace granted access',
    granted: 'Google Workspace has granted access',
  },
  unknown: {
    heading: 'Access was granted',
    granted: 'access has been granted',
  },
}

// DW-CLIENT-HOME-GWS-CONSENT-ERROR-NAMES-WORKSPACE. The failure copy used to
// be a Microsoft story for every cloud: a household that denied or bounced a
// Google Workspace consent was told “Microsoft did not finish the return” and
// “This Microsoft 365 tenant is already connected”. DigitalWatch.md holds
// “Microsoft 365 and Google Workspace” — not 365 alone — so telling the
// Workspace household that Microsoft failed shrinks the firehose to one cloud
// in the one moment they are already unsure. Success already names the cloud;
// the error now names it too. Same three-way rule as the success copy:
// Microsoft for the Microsoft platforms, Google Workspace for the Workspace
// ones, and an unknown platform stays unnamed rather than being dressed up as
// Microsoft. Naming the cloud is not naming a tenant — no tenant, domain or
// address is ever invented here — and an error still does not start a meeting,
// still does not deep-link /client/interview, and still does not unwind the
// success door to /client home where the Word decoy download sits.
//
// DW-CLIENT-HOME-GWS-CONSENT-ERROR-LANDS-ON-HOME. The error variant used to
// keep a second door: a single Link to “/” labelled “Return to sign in”, on
// the reasoning that a failed return had no home to land on. It does. A
// household that denied or bounced Google Workspace — or Microsoft 365 — was
// dumped on the sign-in paint, so it never reached /client home and never saw
// “Download Decoy (.docx)” in that home section. DigitalWatch.md: canaries are
// in the same picture, and the story lives on /client home. Nothing about a
// failed consent makes that picture less true — the tripwire and the decoy are
// theirs either way, and the first meeting is still sitting there. So the
// error primary control is now the same /client door the success variant uses,
// for both clouds alike (naming Workspace never shrank the product; neither
// does this) and for an unknown platform, which lands on the same home rather
// than on LoginPage. ProtectedRoute still owns who may pass, so this is a door
// home, not a claim about the session. The error still does not start a
// meeting, still never deep-links /client/interview, and the failure copy
// still names the cloud the `platform` query actually carries.
const FAILED_HEADING: Record<Cloud, string> = {
  microsoft: 'We couldn’t finish connecting Microsoft 365',
  gws: 'We couldn’t finish connecting Google Workspace',
  unknown: 'We couldn’t finish connecting',
}

// Reason words that carry no cloud at all — a denial is a denial on either
// firehose, so these stay identical rather than being needlessly forked.
const DENIED =
  'It looks like consent wasn’t granted. Nothing has changed on your account.'
const VERIFICATION_FAILED =
  'We couldn’t verify the connection after consent. Please try again.'
const INVALID_LINK =
  'This onboarding link is no longer valid. Please start again.'

// The backend's SMB failure outcomes (smb_onboarding.complete_consent) are a
// small, safe vocabulary — map each to plain-language copy. `error` from the
// wizard flow is currently always `consent_denied`. Anything unrecognised
// falls back to a calm generic line rather than echoing a raw code. The three
// outcomes that describe the cloud's own return (`return_failed`,
// `duplicate`, `domain_mismatch`) are the ones that must name the right cloud.
const REASONS: Record<Cloud, Record<string, string>> = {
  microsoft: {
    consent_denied: DENIED,
    denied: DENIED,
    // Backend fail-soft when Microsoft bounced without a usable signed state
    // (DW-CONSENT-CALLBACK-NO-STATE). Honest: no grant detected, no tenant
    // connected.
    return_failed: 'Microsoft did not finish the return. No tenant was connected.',
    duplicate: 'This Microsoft 365 tenant is already connected.',
    domain_mismatch:
      'The tenant that granted consent didn’t match the one we expected.',
    verification_failed: VERIFICATION_FAILED,
    invalid: INVALID_LINK,
  },
  gws: {
    consent_denied: DENIED,
    denied: DENIED,
    // Same fail-soft, told as the Workspace household's own story. “Account”,
    // not “tenant” — and never a domain we would have had to invent.
    return_failed:
      'Google Workspace did not finish the return. No account was connected.',
    duplicate: 'This Google Workspace account is already connected.',
    domain_mismatch:
      'The Google Workspace account that granted consent didn’t match the one we expected.',
    verification_failed: VERIFICATION_FAILED,
    invalid: INVALID_LINK,
  },
  // Unnamed on purpose. An unknown platform gets the honest, cloud-free line;
  // it is never dressed up as Microsoft just because Microsoft came first.
  unknown: {
    consent_denied: DENIED,
    denied: DENIED,
    return_failed: 'The return didn’t finish. Nothing was connected.',
    duplicate: 'This account is already connected.',
    domain_mismatch:
      'The account that granted consent didn’t match the one we expected.',
    verification_failed: VERIFICATION_FAILED,
    invalid: INVALID_LINK,
  },
}

function reasonFor(code: string | null, cloud: Cloud): string {
  const copy = code ? REASONS[cloud][code] : undefined
  if (copy) return copy
  return 'We couldn’t complete the connection. Please try again, or contact your security team.'
}

export function ConsentResultPage({ variant }: { variant: 'success' | 'error' }) {
  const [params] = useSearchParams()
  const { pathname } = useLocation()
  // Wizard flow uses `error`; SMB flow uses `outcome`. Either carries the code.
  const code = params.get('error') ?? params.get('outcome')

  const success = variant === 'success'
  const cloud = cloudFor(params.get('platform'), pathname)
  const { heading, granted } = GRANTED[cloud]
  // BRIEF-B8: the SMB self-serve Allow (and only it) is followed by the
  // owner choosing who is watched, then the staff-notice step. The wizard's
  // /onboarding/success shares this page and never shows it.
  const smbSession =
    success && pathname === '/smb/consent-complete' ? params.get('session') : null
  const [invited, setInvited] = useState(false)
  useEffect(() => {
    if (!smbSession) return
    let live = true
    fetch(`${API_BASE_URL}/api/v1/smb-onboarding/${encodeURIComponent(smbSession)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (live && body?.owner_invitation_sent === true) setInvited(true)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [smbSession])

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center"
        role="status"
        aria-live="polite"
      >
        {success ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-success/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-success" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              {heading}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Thank you — your Allow was recorded and {granted}. Nothing has
              started yet: granting access is a technical step, not your first
              meeting.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              The next step is your first meeting — the interview. It’s
              waiting on your home page, in the same picture as your
              tripwires and the decoy document you can download.
            </p>
            {invited && <p className="text-sm text-navy mt-2">{OWNER_INVITED_LINE}</p>}
            {smbSession && <SmbSeatsStep session={smbSession} />}
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-danger-light flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-danger" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              {FAILED_HEADING[cloud]}
            </h1>
            <p className="text-sm text-gray-500 mt-2">{reasonFor(code, cloud)}</p>
            <p className="text-sm text-gray-500 mt-2">
              Your home page is still yours. Your tripwires and the decoy
              document you can download are waiting there, in the same
              picture, whenever you want to try the connection again.
            </p>
          </>
        )}

        {/* One door, and it is /client home — where the first meeting sits in
            place with the Word decoy download beside it. The same door for
            both clouds and for both variants: a Google Workspace return lands
            on the same holding picture a Microsoft one does, and a return that
            failed still has a home to land on. Never “/” (the sign-in paint
            they came from) and never the deep link /client/interview, which
            ProtectedRoute and ClientInterviewGate still own. */}
        <Link
          to="/client"
          className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
        >
          Go to your home
        </Link>
      </motion.div>
    </div>
  )
}
